import { PageWrapper, SeoHead } from 'layouts';
import { EventIds } from 'lib';
import { getAuthorProfileData } from 'lib/authors';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { AuthorProfileQuery } from 'types';
import { MappedPost } from 'ui';
import { logger } from 'utils';
import { AuthorProfile } from 'components';
import { sanitizeCategorySeoHead } from 'utils';

interface AuthorPostsPageProps {
  user: AuthorProfileQuery['user'] | null;
  posts: MappedPost[];
  totalAmountOfPosts: number;
  page: string;
  error: {} | null;
}

const AuthorPostsPage: NextPage<AuthorPostsPageProps> = (props) => {
  const { user, posts, totalAmountOfPosts, page, error } = props;

  if (!user) {
    return null;
  }

  const sanitizedSeoHead = sanitizeCategorySeoHead(
    user?.seo?.canonical,
    user?.seo?.fullHead,
    page,
    totalAmountOfPosts
  );

  return (
    <PageWrapper headerColor="white">
      <SeoHead seoHead={sanitizedSeoHead} />
      <AuthorProfile
        user={user}
        posts={posts}
        totalAmountOfPosts={totalAmountOfPosts}
        page={page}
        error={error}
      ></AuthorProfile>
    </PageWrapper>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async (context) => {
  const { data, error, page, totalAmountOfPosts, posts } =
    await getAuthorProfileData(context);

  if (data !== null && error) {
    logger.error(
      'Getting error on AuthorProfile post fetch',
      EventIds.AuthorProfileError,
      error
    );
  }

  if (data === null) {
    return {
      redirect: {
        destination: context.locale !== 'en' ? `/${context.locale}` : '/',
        permanent: true,
      },
    };
  }

  return {
    props: { user: data.user, posts, totalAmountOfPosts, page, error },
    revalidate: 3600, // 1 hour
  };
};

export default AuthorPostsPage;
